import React from 'react';
import { shortenLargeNumbers } from '../../utils/utils';

const formatIfNumber = (val) => (!isNaN(val) ? shortenLargeNumbers(val) : val);

/**
 * Curried custom tooltip.
 * @example:
 * const rows = [
 *   {title: 'Easy', dataKey: 'ease_of_exploit', className: 'orange bold'},
 *   {title: 'Difficult', dataKey: 'ease_of_exploit'},
 * ];
 * const MyCustomTooltip = CustomTooltip({rows, total: true, headingFormatter: (label, payload) => `${label} days ago`});
 * // ...later inside the chart component //
 *   <Tooltip content={MyCustomTooltip} />
 *
 *
 *   customSubPayload is assuming one of the values in the data is an array of:
 *   {customSubPayloadTitleKey:title, customSubPayloadValueKey: value} objects to be displayed instead of
 *   actual chart data. See security incidents over time chart
 *
 */
export const CustomTooltip = ({
  stripEmpties,
  emptyMessage,
  customSubPayloadValueKey,
  customSubPayloadTitleKey,
  customSubPayloadKey,
  rows,
  total,
  headingFormatter,
  icon
}) => ({ type, payload, label, formatter, labelFormatter }) => {
  emptyMessage = emptyMessage || '';
  let totalCount = 0;
  let subPayload = payload && payload.length > 0 && payload[0].payload;
  if (customSubPayloadKey) {
    subPayload = subPayload && subPayload[customSubPayloadKey];
  }
  if (total && subPayload) {
    rows.forEach((row) => (totalCount += subPayload[row.dataKey]));
  }

  const filteredRows = rows
    .map((row, i) => {
      const pData = payload?.find((prow) =>
        row.actualDataKey
          ? prow.dataKey === row.actualDataKey
          : prow.dataKey === row.dataKey
      );
      let correctedVal = '';
      if (subPayload) {
        correctedVal =
          subPayload[row.dataKey] || subPayload[row.fallbackDataKey];
      }

      row.displayVal =
        typeof row.formatter === 'function'
          ? row.formatter(correctedVal, payload)
          : formatIfNumber(correctedVal);

      row.bgColor = (row && row.color) || (pData && pData.color) || null;
      row.styles = (row && row.styles) || (pData && pData.styles) || null;
      row.iconStyles =
        (row && row.iconStyles) || (pData && pData.iconStyles) || null;
      row.textStyles =
        (row && row.textStyles) || (pData && pData.textStyles) || null;
      row.valueStyles =
        (row && row.valueStyles) || (pData && pData.valueStyles) || null;

      return stripEmpties && !row.displayVal ? null : row;
    })
    .filter((f) => !!f);

  return (
    <div className="CustomToolTipV2">
      <div className="heading">
        {headingFormatter && typeof headingFormatter === 'function'
          ? headingFormatter(label, payload)
          : label}
      </div>
      <section className="content">
        {total && (
          <div className="total">
            <span className="row-title">Total</span>
            <span className="row-value">{shortenLargeNumbers(totalCount)}</span>
          </div>
        )}
        {filteredRows && filteredRows.length > 0 ? (
          filteredRows.map((row, i) => (
            <div
              className={`row ${row.className || ''}`}
              key={i}
              style={row.styles}>
              {!!icon && (
                <span
                  className={`icon ${icon}`}
                  style={{
                    ...(row.iconStyles || {}),
                    backgroundColor: row.bgColor
                  }}
                />
              )}
              <span className="row-title" style={row.textStyles}>
                {row.title}
              </span>
              <span className="row-value" style={row.valueStyles}>
                {row.displayVal || 0}
              </span>
            </div>
          ))
        ) : (
          <span className="empty-state-text">{emptyMessage}</span>
        )}
      </section>
    </div>
  );
};

export default CustomTooltip;
